import { Alert, Box, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { commissionTransfersApi, type CommissionTransfer } from '../../api/commission-transfers.api.js';
import { ClientMoneyPositionCards } from '../commissions/ClientMoneyPositionCards.js';
import { TransferLogTable } from '../commissions/TransferLogTable.js';

import { periodLabel } from './report-format.js';

/**
 * Reports → Commissions (DEV-128): the client-money and revenue accounts and the
 * transfers between them in the period. ADMIN/COMPLIANCE only — callers must not
 * render or enable any of this for Finance.
 */

const PAGE_LIMIT = 200;
const MAX_PAGES = 10;

export interface TransfersInPeriod { items: CommissionTransfer[]; truncated: boolean }

/** Every transfer created in the period, inclusive YYYY-MM-DD days (up to MAX_PAGES × PAGE_LIMIT). */
export function useTransfersInPeriod(fromDay: string, toDay: string, enabled: boolean): UseQueryResult<TransfersInPeriod> {
  return useQuery({
    queryKey: ['report-commission-transfers', fromDay, toDay],
    enabled, staleTime: 30_000, retry: 1,
    queryFn: async (): Promise<TransfersInPeriod> => {
      const items: CommissionTransfer[] = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const result = await commissionTransfersApi.list({ from: fromDay, to: toDay, page, limit: PAGE_LIMIT });
        items.push(...result.items);
        if (!result.hasNext) return { items, truncated: false };
      }
      return { items, truncated: true };
    },
  });
}

/** Net money moved to the revenue account per currency: settled forward transfers minus settled reversals. */
export function transferredByCurrency(transfers: readonly CommissionTransfer[]): Array<{ currency: string; cents: bigint }> {
  const totals = new Map<string, bigint>();
  for (const t of transfers) {
    if (t.status !== 'SETTLED') continue;
    const signed = t.kind === 'REVERSE' ? -BigInt(t.amountCents) : BigInt(t.amountCents);
    totals.set(t.currency, (totals.get(t.currency) ?? 0n) + signed);
  }
  return [...totals.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, cents]) => ({ currency, cents }));
}

interface SectionProps { from: string; through: string; transfers: UseQueryResult<TransfersInPeriod> }

export function CommissionAccountsSection({ from, through, transfers }: SectionProps): JSX.Element {
  const position = useQuery({ queryKey: ['client-money-position'], queryFn: () => commissionTransfersApi.position(), staleTime: 30_000 });
  return <Stack spacing={3} sx={{ minWidth: 0 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>Accounts</Typography>
      {position.isLoading && <Stack direction="row" spacing={1.5} alignItems="center" role="status"><CircularProgress size={22} /><Typography>Reading balances…</Typography></Stack>}
      {position.isError && <Alert severity="error">{position.error instanceof Error ? position.error.message : 'The client-money position could not be loaded.'}</Alert>}
      {position.data && <ClientMoneyPositionCards currencies={position.data.currencies} />}
    </Box>
    <Paper sx={{ overflow: 'hidden', minWidth: 0 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ p: 2 }}>
        <Typography variant="h6">Transfers to revenue account</Typography>
        <Typography variant="body2" color="text.secondary">{periodLabel(from, through)} ·{' '}
          <Link component={RouterLink} to="/commissions?tab=transfers">Manage in Fees & Commissions</Link></Typography>
      </Stack>
      {transfers.isError && <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{transfers.error instanceof Error ? transfers.error.message : 'The transfer log could not be loaded.'}</Alert>}
      {transfers.isLoading && <Stack sx={{ p: 2 }} role="status"><CircularProgress size={22} /></Stack>}
      {transfers.data && <>
        {transfers.data.truncated && <Alert severity="info" sx={{ mx: 2, mb: 2 }}>Showing the first {PAGE_LIMIT * MAX_PAGES} transfers of the period.</Alert>}
        <TransferLogTable transfers={transfers.data.items} canResolve={false} />
      </>}
    </Paper>
  </Stack>;
}
